import {useQueryClient} from '@tanstack/react-query';
import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useRef} from 'react';
import {useTranslation} from 'tolgee';

import {useCancelImageSelectionPipelineMutation} from '~api/automl.mutations';
import {AutoMlQueryKeys, useLatestImageSelectionPipelineQuery} from '~api/automl.queries';
import {pushRoute, replaceRoute, Route as RouteEnum, viewPipelineIdQueryParam} from '~utils/routeUtil';
import {ImageOrderBy} from '~redux/types/images';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {showNotification} from '~components/common/Notification';
import {BaseLayout} from '~components/_layout/BaseLayout';
import {LayoutContent} from '~components/_layout/layoutComponents';
import {ModelFooter} from '~components/models/ModelFooter';
import {ModelWorkflowStep} from '~components/models/ModelWorkflowSteps';
import {SmartSelectLoading} from '~components/models/samples/smart-select/SmartSelectLoading';
import {SmartSelectSuccess} from '~components/models/samples/smart-select/SmartSelectSuccess';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {ModelProvider} from 'src/contexts/ModelProvider';

export const Route = createFileRoute('/model/samples/smart-select')({
  component: SmartSelectPage,
});

function SmartSelectPage() {
  return (
    <ModelProvider mode='model-pages'>
      <BaseLayout showBackButton customContentAndFooter>
        <SmartSelectContent />
      </BaseLayout>
    </ModelProvider>
  );
}

function SmartSelectContent() {
  const {t} = useTranslation('common');
  const queryClient = useQueryClient();
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();
  const hasSeenRunning = useRef(false);

  // Invalidate stale cache on mount so we get a fresh fetch.
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: AutoMlQueryKeys.latestImageSelectionPipeline(projectId, currentModel?.id),
    });
  }, [queryClient, projectId, currentModel?.id]);

  const selectionPipelineQuery = useLatestImageSelectionPipelineQuery({modelId: currentModel?.id});
  const hasFreshData = selectionPipelineQuery.isFetchedAfterMount;
  const selectionPipeline = selectionPipelineQuery.data;
  const selectionJobStatus = selectionPipeline?.statusData?.job?.status;
  const selectionPipelineState = selectionPipeline?.statusData?.pipeline?.status;
  const isSelectionCanceled = selectionJobStatus === 'canceled' || !!selectionPipeline?.statusData?.canceled;
  const isSelectionJobFailed = selectionJobStatus === 'failed';
  // Fall back to pipeline state when job is not yet recorded (early-trigger race window).
  const isSelectionRunning =
    !isSelectionCanceled &&
    (selectionJobStatus === 'pending' ||
      selectionJobStatus === 'running' ||
      (selectionJobStatus === undefined &&
        (selectionPipelineState === 'pending' || selectionPipelineState === 'running')));
  const isSelectionComplete = selectionJobStatus === 'success' || selectionPipelineState === 'completed';
  const selectionPipelineTag = selectionPipeline?.curationPipelineTag;
  const selectionJobLink = selectionPipeline?.jobLink;

  useLatestImageSelectionPipelineQuery({
    modelId: currentModel?.id,
    refetchInterval: isSelectionRunning ? 5000 : undefined,
  });

  const cancelSelectionMutation = useCancelImageSelectionPipelineMutation();

  useEffect(() => {
    if (isSelectionRunning) {
      hasSeenRunning.current = true;
    }
  }, [isSelectionRunning]);

  useEffect(() => {
    if (!hasFreshData) return;
    if (isSelectionRunning || isSelectionComplete) return;

    if (isSelectionJobFailed) {
      showNotification({message: t('pipelineFailed'), severity: 'error', id: 'selection-pipeline-failed'});
      replaceRoute(RouteEnum.modelSamples);
    } else if (isSelectionCanceled || !hasSeenRunning.current) {
      replaceRoute(RouteEnum.modelSamples);
    }
  }, [hasFreshData, isSelectionRunning, isSelectionComplete, isSelectionCanceled, isSelectionJobFailed, t]);

  const handleCancel = () => {
    if (selectionPipeline?.pipelineId && currentModel?.id) {
      cancelSelectionMutation.mutate({pipelineId: selectionPipeline.pipelineId, modelId: currentModel.id});
    }
    queryClient.setQueryData(AutoMlQueryKeys.latestImageSelectionPipeline(projectId, currentModel?.id), null);
    pushRoute(RouteEnum.modelSamples);
  };

  const handleShowResults = () => {
    pushRoute(RouteEnum.modelSamplesSmartSelectResults, {
      query: selectionPipelineTag
        ? {
            orderBy: ImageOrderBy.CURATION_SCORE_DESC,
            curationPipelineTag: selectionPipelineTag,
            [viewPipelineIdQueryParam]: selectionPipeline?.pipelineId,
          }
        : undefined,
    });
  };

  useEffect(() => {
    if (hasFreshData && isSelectionComplete && !hasSeenRunning.current) {
      handleShowResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFreshData, isSelectionComplete]);

  if (isSelectionComplete && hasSeenRunning.current) {
    return (
      <>
        <LayoutContent sx={{justifyContent: 'center', alignItems: 'center'}}>
          <SmartSelectSuccess onShowResults={handleShowResults} />
        </LayoutContent>
        <ModelFooter activeStep={ModelWorkflowStep.addSamples} />
      </>
    );
  }

  return (
    <>
      <LayoutContent sx={{justifyContent: 'center', alignItems: 'center'}}>
        <SmartSelectLoading onCancel={handleCancel} jobLink={selectionJobLink || undefined} />
      </LayoutContent>
      <ModelFooter activeStep={ModelWorkflowStep.addSamples} />
    </>
  );
}
